import jwtDecode, { JwtPayload } from 'jwt-decode'

import { config } from '@fairhq/common'

import { AuthErrorEnum } from './types'

/**
 * Get expiration time from token in milliseconds
 */
const getTokenExpirationTime = (token: string): number | null => {
  const decoded = jwtDecode<JwtPayload>(token)
  return decoded?.exp ? decoded.exp * 1000 : null // Convert to milliseconds
}

/**
 * Get issued at time from token in milliseconds
 */
const getTokenIssuedAtTime = (token: string): number | null => {
  const decoded = jwtDecode<JwtPayload>(token)
  return decoded?.iat ? decoded.iat * 1000 : null // Convert to milliseconds
}

/**
 * Check if token is expired
 */
export const isTokenExpired = (token: string): boolean => {
  const expirationTime = getTokenExpirationTime(token)
  return expirationTime ? Date.now() >= expirationTime : true
}

/**
 * Check if token is past part of its lifetime
 */
export const isTokenNeedsRefresh = (token: string): boolean => {
  const expirationTime = getTokenExpirationTime(token)
  const issuedAtTime = getTokenIssuedAtTime(token)

  if (!expirationTime || !issuedAtTime) {
    return true
  }

  // Calculate total token lifetime and current elapsed time
  const totalLifetime = expirationTime - issuedAtTime
  const elapsedTime = Date.now() - issuedAtTime

  return elapsedTime > totalLifetime * 0.5
}

/**
 * Get the token from localStorage
 */
export const getStoredToken = (): string | null =>
  localStorage.getItem(config.tokenKey)

/**
 * Store token in localStorage
 */
export const storeToken = (token: string): void => {
  localStorage.setItem(config.tokenKey, token)
}

/**
 * Remove token from localStorage
 */
export const removeToken = (): void => {
  localStorage.removeItem(config.tokenKey)
}

/**
 * Get one of the covered for auth status
 */
export const getAuthReasonStatus = (reason: AuthErrorEnum) => {
  switch (reason) {
    case AuthErrorEnum.EXPIRED:
      return AuthErrorEnum.EXPIRED
    case AuthErrorEnum.LOGOUT:
      return AuthErrorEnum.LOGOUT
    case AuthErrorEnum.INVALID:
      return AuthErrorEnum.INVALID
    case AuthErrorEnum.SETUP:
      return AuthErrorEnum.SETUP
    default:
      return AuthErrorEnum.ERROR
  }
}
